import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Container } from "reactstrap";
import { Camera, Edit, Plus, Video } from "react-feather";
import Breadcrumb from "../common/breadcrumb";
import { fetchBranches } from "../../utils/apiCalls";
import { getLanes, getDriveThruDevices, getDriveThruMeta, assignDeviceCapturePoint } from "../../utils/driveThruApi";
import { successToast } from "../../shared/Toast";
import { C, tbl, reportingGlobalCSS } from "../reporting/reportingStyles";
import { Tile, RCard, Badge, Loader as RLoader } from "../reporting/ReportingShared";
import LaneModal from "./LaneModal";
import CapturePointModal from "./CapturePointModal";

const CP_STATUS = {
  online: { color: C.green, bg: C.greenLight },
  offline: { color: C.coral, bg: C.coralLight },
  unknown: { color: C.textMed, bg: C.bg },
};

const filterSelectStyle = {
  padding: "7px 12px", fontSize: 12, borderRadius: 8, border: `1.5px solid ${C.border}`,
  fontFamily: "inherit", outline: "none", color: C.text, background: C.white, cursor: "pointer", minWidth: 220,
};
const coralBtn = {
  background: C.coral, border: "none", borderRadius: 10, fontWeight: 600, fontSize: 13,
  color: C.white, padding: "8px 16px", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6,
};
const ghostBtn = {
  background: C.white, border: `1px solid ${C.border}`, borderRadius: 8, fontWeight: 600, fontSize: 12,
  color: C.textMed, padding: "6px 12px", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 5,
};

// e.g. "17 Jun 2026, 3:18:44 PM"
const formatDateTime = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  const month = d.toLocaleString("en-US", { month: "short" });
  let h = d.getHours();
  const m = String(d.getMinutes()).padStart(2, "0");
  const s = String(d.getSeconds()).padStart(2, "0");
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${d.getDate()} ${month} ${d.getFullYear()}, ${h}:${m}:${s} ${ampm}`;
};

const DriveThruSetup = () => {
  const userType = localStorage.getItem("userType");
  const merchantId = localStorage.getItem("MerchantId");
  const { selectedMerchant: reduxSelectedMerchant } = useSelector((state) => state.merchant || {});
  const selectedMerchant = userType === "merchant" ? merchantId : reduxSelectedMerchant;

  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState("");
  const [lanes, setLanes] = useState([]);
  const [devices, setDevices] = useState([]);
  const [meta, setMeta] = useState({ cameraBrands: [], capturePointTypes: [] });
  const [loading, setLoading] = useState(false);

  const [laneModal, setLaneModal] = useState({ open: false, lane: null });
  const [cpModal, setCpModal] = useState({ open: false, capturePoint: null, laneId: null });

  const loadBranches = useCallback(async () => {
    const res = await fetchBranches({}, false);
    let list = res?.data || [];
    if (selectedMerchant && selectedMerchant !== "all") {
      list = list.filter((b) => String(b.MerchantId) === String(selectedMerchant));
    }
    setBranches(list);
  }, [selectedMerchant]);

  const loadMeta = useCallback(async () => setMeta(await getDriveThruMeta()), []);

  const loadLanes = useCallback(async () => {
    if (!selectedBranch) return setLanes([]);
    setLanes((await getLanes({ branchId: selectedBranch })) || []);
  }, [selectedBranch]);

  const loadDevices = useCallback(async () => {
    if (!selectedBranch) return setDevices([]);
    setDevices((await getDriveThruDevices({ branchId: selectedBranch })) || []);
  }, [selectedBranch]);

  useEffect(() => {
    loadBranches();
    loadMeta();
  }, [loadBranches, loadMeta]);

  useEffect(() => {
    (async () => {
      if (!selectedBranch) {
        setLanes([]);
        setDevices([]);
        return;
      }
      setLoading(true);
      await Promise.all([loadLanes(), loadDevices()]);
      setLoading(false);
    })();
  }, [selectedBranch, loadLanes, loadDevices]);

  // Auto-refresh camera health + device online status every 60s while a branch is selected.
  useEffect(() => {
    if (!selectedBranch) return undefined;
    const interval = setInterval(() => {
      loadLanes();
      loadDevices();
    }, 60000);
    return () => clearInterval(interval);
  }, [selectedBranch, loadLanes, loadDevices]);

  const allCapturePoints = useMemo(
    () => lanes.flatMap((lane) => (lane.CapturePoints || []).map((cp) => ({ ...cp, laneName: lane.name }))),
    [lanes]
  );

  const stats = useMemo(() => {
    const order = allCapturePoints.filter((cp) => cp.type === "ORDER").length;
    const pickup = allCapturePoints.filter((cp) => cp.type === "PICKUP").length;
    const assigned = devices.filter((d) => d.assignedCapturePointId).length;
    return { lanes: lanes.length, order, pickup, devices: devices.length, assigned };
  }, [lanes, allCapturePoints, devices]);

  const handleAssign = async (deviceId, value) => {
    const result = await assignDeviceCapturePoint(deviceId, value ? Number(value) : null);
    if (result) {
      successToast("Device assignment updated");
      loadDevices();
    }
  };

  return (
    <Fragment>
      <Breadcrumb title="Drive-Thru Setup" parent="Drive-Thru" />
      <Container fluid>
        <style>{reportingGlobalCSS}</style>
        <div className="rpt-wrap">
          {/* Filter bar */}
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
            <select style={filterSelectStyle} value={selectedBranch} onChange={(e) => setSelectedBranch(e.target.value)}>
              <option value="">Select Branch</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
            <div style={{ flex: 1 }} />
            {selectedBranch && (
              <button style={coralBtn} onClick={() => setLaneModal({ open: true, lane: null })}>
                <Plus size={15} /> Add Lane
              </button>
            )}
          </div>

          {!selectedBranch ? (
            <RCard>
              <div style={{ textAlign: "center", color: C.textLight, padding: 30 }}>
                Select a branch to manage its drive-thru lanes.
              </div>
            </RCard>
          ) : loading ? (
            <RLoader />
          ) : (
            <>
              {/* KPI tiles */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 16, marginBottom: 24 }}>
                <Tile label="Lanes" value={stats.lanes} icon="🛣️" gradient={`linear-gradient(135deg, ${C.coral}, #FF8A80)`} delay={0} />
                <Tile label="Order Points" value={stats.order} icon="🎤" gradient={`linear-gradient(135deg, ${C.blue}, #A8B8FF)`} delay={60} />
                <Tile label="Pickup Points" value={stats.pickup} icon="📦" gradient={`linear-gradient(135deg, ${C.orange}, #FFD200)`} delay={120} />
                <Tile label="POS Devices" value={stats.devices} icon="📱" gradient={`linear-gradient(135deg, ${C.green}, #38F9D7)`} delay={180} />
                <Tile label="Assigned" value={stats.assigned} icon="🔗" gradient={`linear-gradient(135deg, ${C.purple}, #C39BD3)`} delay={240} />
              </div>

              {/* Lanes + capture points */}
              {lanes.length === 0 ? (
                <RCard>
                  <div style={{ textAlign: "center", color: C.textLight, padding: 24 }}>
                    No lanes yet. Add a lane, then add its order/pickup capture points.
                  </div>
                </RCard>
              ) : (
                lanes.map((lane) => (
                  <RCard key={lane.id}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
                      <Camera size={18} color={C.coral} />
                      <span style={{ fontSize: 15, fontWeight: 700, color: C.text }}>{lane.name}</span>
                      <Badge color={lane.isActive ? C.green : C.textMed} bg={lane.isActive ? C.greenLight : C.bg}>
                        {lane.isActive ? "Active" : "Inactive"}
                      </Badge>
                      <div style={{ flex: 1 }} />
                      <button style={ghostBtn} onClick={() => setLaneModal({ open: true, lane })}>
                        <Edit size={13} /> Edit
                      </button>
                      <button style={coralBtn} onClick={() => setCpModal({ open: true, capturePoint: null, laneId: lane.id })}>
                        <Plus size={14} /> Capture Point
                      </button>
                    </div>

                    {(lane.CapturePoints || []).length === 0 ? (
                      <div style={{ color: C.textLight, fontSize: 13 }}>No capture points on this lane.</div>
                    ) : (
                      <div style={{ overflowX: "auto", borderRadius: 10, border: `1px solid ${C.border}` }}>
                        <table style={tbl.table}>
                          <thead>
                            <tr>
                              <th style={tbl.th}>Type</th>
                              <th style={tbl.th}>Brand</th>
                              <th style={tbl.th}>Camera</th>
                              <th style={tbl.th}>Channel</th>
                              <th style={tbl.th}>Password</th>
                              <th style={tbl.th}>Stop box</th>
                              <th style={tbl.th}>Status</th>
                              <th style={tbl.th}>Active</th>
                              <th style={{ ...tbl.th, textAlign: "right" }} />
                            </tr>
                          </thead>
                          <tbody>
                            {lane.CapturePoints.map((cp) => {
                              const st = CP_STATUS[cp.lastStatus] || CP_STATUS.unknown;
                              return (
                                <tr key={cp.id} className="rpt-row">
                                  <td style={tbl.td}>
                                    <Badge
                                      color={cp.type === "ORDER" ? C.blue : C.orange}
                                      bg={cp.type === "ORDER" ? C.blueLight : C.orangeLight}
                                    >
                                      {cp.type}
                                    </Badge>
                                    {cp.testMode ? (
                                      <span style={{ marginLeft: 6 }}>
                                        <Badge color={C.purple} bg={C.purpleLight}>TEST</Badge>
                                      </span>
                                    ) : null}
                                  </td>
                                  <td style={tbl.td}>{cp.cameraBrand}</td>
                                  <td style={{ ...tbl.td, fontWeight: 600 }}>
                                    {cp.cameraIp}:{cp.cameraHttpPort}
                                  </td>
                                  <td style={tbl.td}>{cp.cameraChannel}</td>
                                  <td style={tbl.td}>
                                    {cp.hasPassword ? (
                                      <Badge color={C.green} bg={C.greenLight}>Set</Badge>
                                    ) : (
                                      <Badge color={C.textLight} bg={C.bg}>—</Badge>
                                    )}
                                  </td>
                                  <td style={tbl.td}>
                                    {cp.stopBoxRegion ? (
                                      <Badge color={C.green} bg={C.greenLight}>Set</Badge>
                                    ) : (
                                      <Badge color={C.textLight} bg={C.bg}>—</Badge>
                                    )}
                                  </td>
                                  <td style={tbl.td} title={cp.lastOnlineAt ? `Last online: ${new Date(cp.lastOnlineAt).toLocaleString()}` : "No data yet"}>
                                    <Badge color={st.color} bg={st.bg}>{cp.lastStatus || "unknown"}</Badge>
                                  </td>
                                  <td style={{ ...tbl.td, color: C.textMed }}>{cp.isActive ? "Yes" : "No"}</td>
                                  <td style={{ ...tbl.td, textAlign: "right" }}>
                                    <button style={ghostBtn} onClick={() => setCpModal({ open: true, capturePoint: cp, laneId: lane.id })}>
                                      <Edit size={13} />
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </RCard>
                ))
              )}

              {/* POS device assignment */}
              <RCard title={<><span>POS Devices</span><span style={{ fontWeight: 500, fontSize: 12, color: C.textLight, marginLeft: 8 }}>Assign each device to the capture point its "Get car details" button should use.</span></>}>
                {devices.length === 0 ? (
                  <div style={{ color: C.textLight, fontSize: 13 }}>No POS devices registered for this branch.</div>
                ) : (
                  <div style={{ overflowX: "auto", borderRadius: 10, border: `1px solid ${C.border}` }}>
                    <table style={tbl.table}>
                      <thead>
                        <tr>
                          <th style={tbl.th}>Device</th>
                          <th style={tbl.th}>Cashbox</th>
                          <th style={tbl.th}>Status</th>
                          <th style={tbl.th}>Last Updated</th>
                          <th style={{ ...tbl.th, width: 320 }}>Assigned Capture Point</th>
                        </tr>
                      </thead>
                      <tbody>
                        {devices.map((d) => (
                          <tr key={d.id} className="rpt-row">
                            <td style={{ ...tbl.td, fontWeight: 700, color: C.text }}>
                              {d.name || "—"}
                              <div style={{ fontSize: 11, fontWeight: 400, color: C.textLight }}>{d.merchant_device_id}</div>
                            </td>
                            <td style={{ ...tbl.td, color: C.textMed }}>{d.cashboxName || "—"}</td>
                            <td style={tbl.td}>
                              <Badge
                                color={d.isConnected ? C.green : C.coral}
                                bg={d.isConnected ? C.greenLight : C.coralLight}
                              >
                                {d.isConnected ? "Online" : "Offline"}
                              </Badge>
                            </td>
                            <td style={{ ...tbl.td, color: C.textMed, whiteSpace: "nowrap" }}>
                              {formatDateTime(d.updatedAt)}
                            </td>
                            <td style={tbl.td}>
                              <select
                                style={{ ...filterSelectStyle, minWidth: 280 }}
                                value={d.assignedCapturePointId || ""}
                                onChange={(e) => handleAssign(d.id, e.target.value)}
                              >
                                <option value="">Not assigned</option>
                                {allCapturePoints.map((cp) => (
                                  <option key={cp.id} value={cp.id}>
                                    {cp.laneName} — {cp.type} ({cp.cameraIp})
                                  </option>
                                ))}
                              </select>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </RCard>

              {/* Live preview / Test ANPR — deferred until POS-capture ships */}
              <RCard>
                <div style={{ display: "flex", alignItems: "center", gap: 10, color: C.textMed, fontSize: 13 }}>
                  <Video size={18} color={C.textLight} />
                  Live camera preview &amp; Test-ANPR will appear here once the POS app's camera capture is live — these
                  run on the branch device (cameras aren't reachable from this panel directly).
                </div>
              </RCard>
            </>
          )}
        </div>
      </Container>

      <LaneModal
        isOpen={laneModal.open}
        toggle={() => setLaneModal({ open: false, lane: null })}
        lane={laneModal.lane}
        branchId={selectedBranch}
        onSaved={loadLanes}
      />
      <CapturePointModal
        isOpen={cpModal.open}
        toggle={() => setCpModal({ open: false, capturePoint: null, laneId: null })}
        capturePoint={cpModal.capturePoint}
        laneId={cpModal.laneId}
        brands={meta.cameraBrands}
        types={meta.capturePointTypes}
        onSaved={loadLanes}
      />
    </Fragment>
  );
};

export default DriveThruSetup;
